import { useState, type ReactNode } from "react";
import { ChevronRight, KeyRound, ShieldCheck, Smartphone } from "lucide-react";

import { useEnrollmentPrompt } from "../../../components/security/EnrollmentPrompt";
import { isPasskeySupported } from "../../../utils/webauthn";
import { Pill } from "../primitives";
import { Button } from "@/v2/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/v2/ui/dialog";
import { EnrollmentDialog } from "./EnrollmentDialog";

/** Offered once after sign-in to accounts without two-step verification */
export function EnrollmentPrompt() {
    const { open, method, overview, snooze, dismissForever, begin, finish } = useEnrollmentPrompt();

    return (
        <>
            <PromptDialog
                open={open}
                passkeys={!!overview?.passkeysAvailable}
                totp={!!overview?.totpAvailable}
                onBegin={begin}
                onSnooze={snooze}
                onDismissForever={dismissForever}
            />
            <EnrollmentDialog open={Boolean(method)} initialMethod={method} onClose={finish} onComplete={() => undefined} />
        </>
    );
}

export function PromptDialog({ open, passkeys, totp, onBegin, onSnooze, onDismissForever }: {
    open: boolean;
    passkeys: boolean;
    totp: boolean;
    onBegin: (method: "totp" | "passkey") => void;
    onSnooze: () => void;
    onDismissForever: () => void;
}) {
    const [confirmingOff, setConfirmingOff] = useState(false);
    const passkeySupported = isPasskeySupported();

    return (
        <Dialog open={open} onOpenChange={next => !next && onSnooze()}>
            <DialogContent data-v2="" className="sm:max-w-md">
                <DialogHeader>
                    <span className="mb-2 grid size-10 place-items-center rounded-xl bg-soft text-text-2 [&_svg]:size-5">
                        <ShieldCheck />
                    </span>
                    <DialogTitle className="font-display text-lg font-semibold">Add two-step verification</DialogTitle>
                    <DialogDescription className="leading-relaxed">
                        It takes about a minute and keeps your account safe if your password leaks.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-2">
                    {passkeys && (
                        <Option
                            icon={<KeyRound />}
                            title="Passkey"
                            badge="Recommended"
                            text={passkeySupported
                                ? "Face ID, Touch ID, your screen lock or a security key."
                                : "This browser can't create passkeys."}
                            disabled={!passkeySupported}
                            onClick={() => onBegin("passkey")}
                        />
                    )}
                    {totp && (
                        <Option
                            icon={<Smartphone />}
                            title="Authenticator app"
                            text="A six-digit code from 1Password, Google Authenticator or Authy."
                            onClick={() => onBegin("totp")}
                        />
                    )}
                </div>

                {confirmingOff ? (
                    <div className="flex flex-col gap-3 rounded-xl bg-soft p-4 sm:flex-row sm:items-center">
                        <p className="flex-1 text-xs leading-relaxed text-text-2">
                            Stop reminding you? You can turn it back on in Account security.
                        </p>
                        <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => setConfirmingOff(false)}>Cancel</Button>
                            <Button size="sm" onClick={onDismissForever}>Turn off</Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between">
                        <Button variant="ghost" className="-ml-2.5" onClick={onSnooze}>Remind me later</Button>
                        <button
                            type="button"
                            onClick={() => setConfirmingOff(true)}
                            className="text-xs font-medium text-text-3 hover:text-text-2"
                        >
                            Don't remind me again
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Option({ icon, title, badge, text, disabled, onClick }: {
    icon: ReactNode;
    title: string;
    badge?: string;
    text: string;
    disabled?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className="group flex w-full items-center gap-4 rounded-xl bg-soft p-4 text-left outline-none transition-colors hover:bg-soft-2 focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-soft"
        >
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-tile text-text-2 [&_svg]:size-[18px]">{icon}</span>
            <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{title}</span>
                    {badge && !disabled && <Pill tone="info">{badge}</Pill>}
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-text-2">{text}</span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-text-3 transition-transform group-hover:translate-x-0.5 group-disabled:hidden" />
        </button>
    );
}
